import React, { lazy, Suspense } from "react";
import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";

import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";

import HomePage from "@/pages/Home";
import LogoDemo from "@/pages/LogoDemo";
import FeaturesPage from "@/pages/Features";
import PricingPage from "@/pages/Pricing";
import AboutPage from "@/pages/About";
import FAQPage from "@/pages/FAQ";
import ContactPage from "@/pages/Contact";
import PrivacyPage from "@/pages/Privacy";
import ImprintPage from "@/pages/Imprint";
import TermsPage from "@/pages/Terms";
import LoginPage from "@/pages/Login";
import RegisterPage from "@/pages/Register";
import ForgotPasswordPage from "@/pages/ForgotPassword";
import PaymentSuccessPage from "@/pages/PaymentSuccess";
import LeadFormPage from "@/pages/LeadForm";
import MagicLoginPage from "@/pages/MagicLogin";
import PartnerPage from "@/pages/Partner";
import CompanyLandingPage from "@/pages/CompanyLanding";
import PublicProfilePage from "@/pages/PublicProfile";
import CookieConsent from "@/components/CookieConsent";
import { initAnalytics } from "@/lib/analytics";

const PartnerDashboard = lazy(() => import("@/pages/PartnerDashboard"));
const DashboardOverview = lazy(() => import("@/pages/dashboard/Overview"));
const QuickGenerator = lazy(() => import("@/pages/dashboard/QuickGenerator"));
const GeneratorPage = lazy(() => import("@/pages/dashboard/Generator"));
const ApplicationsList = lazy(() => import("@/pages/dashboard/ApplicationsList"));
const ApplicationDetail = lazy(() => import("@/pages/dashboard/ApplicationDetail"));
const DocumentsPage = lazy(() => import("@/pages/dashboard/Documents"));
const JobsPage = lazy(() => import("@/pages/dashboard/Jobs"));
const FeedPage = lazy(() => import("@/pages/dashboard/Feed"));
const CompanyDashboard = lazy(() => import("@/pages/CompanyDashboard"));
const ProfilePage = lazy(() => import("@/pages/dashboard/Profile"));
const SubscriptionPage = lazy(() => import("@/pages/dashboard/Subscription"));
const TerminePage = lazy(() => import("@/pages/dashboard/Termine"));
const AssistantPage = lazy(() => import("@/pages/dashboard/Assistant"));
const CoachPage = lazy(() => import("@/pages/dashboard/Coach"));
const FavoritenPage = lazy(() => import("@/pages/dashboard/Favoriten"));
const NotificationsPage = lazy(() => import("@/pages/dashboard/Notifications"));
const KontoPage = lazy(() => import("@/pages/dashboard/Konto"));
const ActivityPage = lazy(() => import("@/pages/dashboard/Activity"));
const AdminPage = lazy(() => import("@/pages/Admin"));

initAnalytics();
import SeoLanding from "@/pages/seo/SeoLanding";
import BerufPage from "@/pages/seo/BerufPage";
import BerufeIndex from "@/pages/seo/BerufeIndex";
import { RatgeberIndex, RatgeberArticle } from "@/pages/seo/Ratgeber";
import NotFoundPage from "@/pages/NotFound";
import { LANDINGPAGES } from "@/data/landingpages";

function ProfilRedirect() {
    const loc = useLocation();
    return <Navigate to={{ pathname: "/dashboard/bewerbungsdaten", search: loc.search, hash: loc.hash }} replace />;
}

function App() {
    return (
        <div className="App">
            <HelmetProvider>
                <AuthProvider>
                    <BrowserRouter>
                        <Toaster position="top-right" richColors closeButton />
                        <CookieConsent />
                        <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-sm text-zinc-500">Laden…</div>}>
                        <Routes>
                        {/* Public */}
                        <Route path="/" element={<HomePage />} />
                        <Route path="/logo-demo" element={<LogoDemo />} />
                        <Route path="/funktionen" element={<FeaturesPage />} />
                        <Route path="/preise" element={<PricingPage />} />
                        <Route path="/ueber-uns" element={<AboutPage />} />
                        <Route path="/faq" element={<FAQPage />} />
                        <Route path="/kontakt" element={<ContactPage />} />
                        <Route path="/datenschutz" element={<PrivacyPage />} />
                        <Route path="/impressum" element={<ImprintPage />} />
                        <Route path="/agb" element={<TermsPage />} />
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/registrieren" element={<RegisterPage />} />
                        <Route path="/passwort-vergessen" element={<ForgotPasswordPage />} />
                        <Route path="/zahlung-erfolgreich" element={<PaymentSuccessPage />} />
                        <Route path="/lead" element={<LeadFormPage />} />
                        <Route path="/magic-login" element={<MagicLoginPage />} />
                        <Route path="/partner" element={<PartnerPage />} />
                        <Route path="/p/:shareId" element={<PublicProfilePage />} />
                        <Route path="/partner/dashboard" element={<ProtectedRoute><PartnerDashboard /></ProtectedRoute>} />

                        {/* Protected */}
                        <Route path="/dashboard" element={<ProtectedRoute><DashboardOverview /></ProtectedRoute>} />
                        <Route path="/dashboard/generator" element={<ProtectedRoute><GeneratorPage /></ProtectedRoute>} />
                        <Route path="/dashboard/jobs" element={<ProtectedRoute><JobsPage /></ProtectedRoute>} />
                        <Route path="/dashboard/feed" element={<ProtectedRoute><FeedPage /></ProtectedRoute>} />
                        <Route path="/unternehmen" element={<CompanyLandingPage />} />
                        <Route path="/company" element={<ProtectedRoute><CompanyDashboard /></ProtectedRoute>} />
                        <Route path="/dashboard/generator-pro" element={<ProtectedRoute><GeneratorPage /></ProtectedRoute>} />
                        <Route path="/dashboard/schnell-bewerbung" element={<ProtectedRoute><QuickGenerator /></ProtectedRoute>} />
                        <Route path="/dashboard/bewerbungen" element={<ProtectedRoute><ApplicationsList /></ProtectedRoute>} />
                        <Route path="/dashboard/bewerbungen/:id" element={<ProtectedRoute><ApplicationDetail /></ProtectedRoute>} />
                        <Route path="/dashboard/dokumente" element={<ProtectedRoute><DocumentsPage /></ProtectedRoute>} />
                        <Route path="/dashboard/bewerbungsdaten" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
                        <Route path="/dashboard/profil" element={<ProfilRedirect />} />
                        <Route path="/dashboard/termine" element={<ProtectedRoute><TerminePage /></ProtectedRoute>} />
                        <Route path="/dashboard/ki-assistent" element={<ProtectedRoute><AssistantPage /></ProtectedRoute>} />
                        <Route path="/dashboard/coach" element={<ProtectedRoute><CoachPage /></ProtectedRoute>} />
                        <Route path="/dashboard/favoriten" element={<ProtectedRoute><FavoritenPage /></ProtectedRoute>} />
                        <Route path="/dashboard/benachrichtigungen" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
                        <Route path="/dashboard/konto" element={<ProtectedRoute><KontoPage /></ProtectedRoute>} />
                        <Route path="/dashboard/aktivitaet" element={<ProtectedRoute><ActivityPage /></ProtectedRoute>} />
                        <Route path="/dashboard/abo" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />

                        {/* SEO-Landingpages */}
                        {LANDINGPAGES.map((p) => (
                            <Route key={p.slug} path={`/${p.slug}`} element={<SeoLanding page={p} />} />
                        ))}
                        <Route path="/berufe" element={<BerufeIndex />} />
                        <Route path="/bewerbung/:slug" element={<BerufPage variant="bewerbung" />} />
                        <Route path="/lebenslauf/:slug" element={<BerufPage variant="lebenslauf" />} />
                        <Route path="/ratgeber" element={<RatgeberIndex />} />
                        <Route path="/ratgeber/:slug" element={<RatgeberArticle />} />

                        {/* Admin */}
                            <Route path="/admin" element={<ProtectedRoute adminOnly><AdminPage /></ProtectedRoute>} />

                        <Route path="*" element={<NotFoundPage />} />
                        </Routes>
                        </Suspense>
                    </BrowserRouter>
                </AuthProvider>
            </HelmetProvider>
        </div>
    );
}

export default App;
